import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Tag,
  Space,
  Typography,
  Popconfirm,
  message,
  Upload,
  Radio
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  BookOutlined,
  UploadOutlined,
  DownloadOutlined,
  FileTextOutlined,
  FilePdfOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { documentsService } from '../services/documentsService';
import { storageService } from '../services/storageService';
import { handleError, isOnline } from '../utils/errorHandler';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [languageFilter, setLanguageFilter] = useState('all'); // 'all', 'ru', 'tg'

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [form] = Form.useForm();
  const [online, setOnline] = useState(isOnline());

  // Загрузка документов из Firebase
  useEffect(() => {
    loadDocuments();
    
    // Отслеживание состояния сети
    const handleOnline = () => {
      setOnline(true);
      message.success('Подключение к интернету восстановлено');
      loadDocuments(); // Перезагружаем данные при восстановлении связи
    };
    
    const handleOffline = () => {
      setOnline(false);
      message.warning('Нет подключения к интернету');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const data = await documentsService.getAllDocuments();
      setDocuments(data);
    } catch (error) {
      const errorInfo = handleError(error, 'loadDocuments');
      message.error(errorInfo.message);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Документ',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <Space>
          <BookOutlined style={{ fontSize: '24px', color: '#2196F3' }} />
          <div>
            <div style={{ fontWeight: 'bold' }}>{text}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{record.description}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Тип',
      dataIndex: 'type',
      key: 'type',
      render: (type) => <Tag color="blue">{type}</Tag>
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status === 'active' ? 'Активен' : 'Неактивен'}
        </Tag>
      )
    },
    {
      title: 'Язык',
      dataIndex: 'language',
      key: 'language',
      width: 120,
      render: (language) => {
        const langMap = {
          'Таджикский': { color: 'green', text: '🇹🇯 Тоҷикӣ' },
          'tg': { color: 'green', text: '🇹🇯 Тоҷикӣ' },
          'Русский': { color: 'blue', text: '🇷🇺 Русский' },
          'ru': { color: 'blue', text: '🇷🇺 Русский' },
          'Английский': { color: 'purple', text: '🇬🇧 English' },
        };
        const langConfig = langMap[language] || { color: 'default', text: language || 'Русский' };
        return <Tag color={langConfig.color}>{langConfig.text}</Tag>;
      },
      filters: [
        { text: '🇷🇺 Русский', value: 'ru' },
        { text: '🇹🇯 Тоҷикӣ', value: 'tg' },
      ],
      onFilter: (value, record) => {
        const lang = record.language || 'Русский';
        if (value === 'ru') return lang === 'Русский' || lang === 'ru';
        if (value === 'tg') return lang === 'Таджикский' || lang === 'tg';
        return false;
      },
    },
    {
      title: 'PDF файл',
      key: 'pdfFile',
      width: 120,
      render: (_, record) => {
        const hasPdf = record.fileUrl && record.fileUrl.trim() !== '';
        return (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {hasPdf ? (
              <Tag color="green" icon={<CheckCircleOutlined />}>
                <FilePdfOutlined /> Загружен
              </Tag>
            ) : (
              <Tag color="default" icon={<CloseCircleOutlined />}>
                <FilePdfOutlined /> Нет файла
              </Tag>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Текст',
      key: 'textContent',
      width: 120,
      render: (_, record) => {
        const hasText = record.content && record.content.trim() !== '';
        return (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {hasText ? (
              <Tag color="blue" icon={<CheckCircleOutlined />}>
                <FileTextOutlined /> Есть текст
              </Tag>
            ) : (
              <Tag color="default" icon={<CloseCircleOutlined />}>
                <FileTextOutlined /> Нет текста
              </Tag>
            )}
          </Space>
        );
      }
    },
    {
      title: 'Размер',
      dataIndex: 'fileSize',
      key: 'fileSize',
      render: (fileSize, record) => {
        if (record.fileUrl && record.fileUrl.trim() !== '') {
          return fileSize || '—';
        }
        return '—';
      }
    },
    {
      title: 'Скачивания',
      dataIndex: 'downloads',
      key: 'downloads',
      render: (downloads) => `${(downloads || 0).toLocaleString()}`
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(record)}
          />
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Удалить документ?"
            onConfirm={() => handleDelete(record.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingDocument(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (document) => {
    setEditingDocument(document);
    form.setFieldsValue(document);
    setIsModalVisible(true);
  };

  const handleView = (document) => {
    const hasPdf = document.fileUrl && document.fileUrl.trim() !== '';
    const hasText = document.content && document.content.trim() !== '';
    
    Modal.info({
      title: document.title,
      content: (
        <div>
          <p><strong>Описание:</strong> {document.description}</p>
          <p><strong>Тип:</strong> {document.type}</p>
          <p><strong>Статус:</strong> {document.status === 'active' ? 'Активен' : 'Неактивен'}</p>
          <p><strong>Язык:</strong> {document.language}</p>
          <div style={{ margin: '12px 0', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
            <p style={{ margin: '8px 0', fontWeight: 'bold' }}>Файлы и контент:</p>
            <p style={{ margin: '4px 0' }}>
              <FilePdfOutlined style={{ marginRight: '8px', color: hasPdf ? '#52c41a' : '#999' }} />
              <strong>PDF файл:</strong> {hasPdf ? (
                <Tag color="green" icon={<CheckCircleOutlined />} style={{ marginLeft: '8px' }}>Загружен</Tag>
              ) : (
                <Tag color="default" icon={<CloseCircleOutlined />} style={{ marginLeft: '8px' }}>Не загружен</Tag>
              )}
            </p>
            <p style={{ margin: '4px 0' }}>
              <FileTextOutlined style={{ marginRight: '8px', color: hasText ? '#1890ff' : '#999' }} />
              <strong>Текстовый контент:</strong> {hasText ? (
                <Tag color="blue" icon={<CheckCircleOutlined />} style={{ marginLeft: '8px' }}>Есть текст</Tag>
              ) : (
                <Tag color="default" icon={<CloseCircleOutlined />} style={{ marginLeft: '8px' }}>Нет текста</Tag>
              )}
            </p>
            {hasPdf && <p style={{ margin: '4px 0', marginTop: '8px' }}><strong>Размер файла:</strong> {document.fileSize || '—'}</p>}
          </div>
          <p><strong>Скачивания:</strong> {document.downloads.toLocaleString()}</p>
          <p><strong>Дата создания:</strong> {
            document.createdAt ? (
              document.createdAt.toDate ? 
                document.createdAt.toDate().toLocaleDateString('ru-RU') : 
                new Date(document.createdAt).toLocaleDateString('ru-RU')
            ) : '-'
          }</p>
        </div>
      ),
      width: 500,
    });
  };

  const handleDownload = (document) => {
    message.success(`Скачивание ${document.title}...`);
  };

  const handleDelete = async (id) => {
    try {
      await documentsService.deleteDocument(id);
      setDocuments(documents.filter(document => document.id !== id));
      message.success('Документ удален');
    } catch (error) {
      console.error('Error deleting document:', error);
      const errorInfo = handleError(error, 'deleteDocument');
      message.error(errorInfo.message);
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      // Исключаем файлы из данных для Firestore
      const { file, iconFile, ...documentData } = values;
      
      // Сохраняем iconUrl из выпадающего списка, если он указан и не пустой
      if (documentData.iconUrl && documentData.iconUrl.trim() === '') {
        delete documentData.iconUrl;
      }
      
      let documentId = editingDocument?.id;
      
      // Если создаем новый документ, сначала создаем его для получения ID
      if (!editingDocument) {
        documentId = await documentsService.createDocument({
          ...documentData,
          fileSize: '0 MB'
        });
      }
      
      // Загружаем иконку из файла, если она выбрана (приоритет над стандартной иконкой)
      if (iconFile) {
        let iconFileObj = null;
        
        if (iconFile.fileList && iconFile.fileList.length > 0) {
          iconFileObj = iconFile.fileList[0].originFileObj || iconFile.fileList[0];
        } else if (iconFile.file) {
          iconFileObj = iconFile.file.originFileObj || iconFile.file;
        } else if (iconFile.originFileObj) {
          iconFileObj = iconFile.originFileObj;
        } else if (iconFile instanceof File) {
          iconFileObj = iconFile;
        }
        
        if (iconFileObj) {
          try {
            const iconUrl = await storageService.uploadDocumentIcon(iconFileObj, documentId);
            documentData.iconUrl = iconUrl; // Перезаписываем стандартную иконку загруженной
            message.success('Иконка загружена');
          } catch (error) {
            const errorInfo = handleError(error, 'uploadIcon');
            message.warning(`Не удалось загрузить иконку: ${errorInfo.message}. Документ будет сохранен без иконки.`);
          }
        }
      }
      
      // Загружаем PDF файл, если он выбран
      if (file) {
        let fileObj = null;
        
        if (file.fileList && file.fileList.length > 0) {
          fileObj = file.fileList[0].originFileObj || file.fileList[0];
        } else if (file.file) {
          fileObj = file.file.originFileObj || file.file;
        } else if (file.originFileObj) {
          fileObj = file.originFileObj;
        } else if (file instanceof File) {
          fileObj = file;
        }
        
        if (fileObj) {
          try {
            const fileUrl = await storageService.uploadDocumentFile(fileObj, documentId);
            documentData.fileUrl = fileUrl;
            documentData.fileSize = `${(fileObj.size / 1024 / 1024).toFixed(2)} MB`;
            message.success('PDF файл загружен');
          } catch (error) {
            const errorInfo = handleError(error, 'uploadFile');
            message.warning(`Не удалось загрузить PDF файл: ${errorInfo.message}. Документ будет сохранен без файла.`);
          }
        }
      }
      
      if (editingDocument) {
        // Обновление существующего документа
        await documentsService.updateDocument(editingDocument.id, documentData);
        message.success('Документ обновлен');
      } else {
        // Обновляем документ с загруженными файлами
        if (documentData.iconUrl || documentData.fileUrl) {
          await documentsService.updateDocument(documentId, documentData);
        }
        message.success('Документ добавлен');
      }
      
      await loadDocuments(); // Перезагружаем список
      setIsModalVisible(false);
      form.resetFields();
      setEditingDocument(null);
    } catch (error) {
      const errorInfo = handleError(error, 'saveDocument');
      message.error(errorInfo.message);
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingDocument(null);
  };

  return (
    <div>
      {!online && (
        <div style={{
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <span style={{ fontSize: '18px' }}>⚠️</span>
          <span style={{ color: '#856404' }}>
            Нет подключения к интернету. Некоторые функции могут быть недоступны.
          </span>
        </div>
      )}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2}>Управление законами</Title>
          <p style={{ color: '#666', margin: 0 }}>Управляйте правовыми документами и материалами</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          Добавить документ
        </Button>
      </div>

      {/* Фильтр по языку */}
      <Card style={{ marginBottom: '16px' }}>
        <Space size="middle" align="center">
          <GlobalOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
          <span style={{ fontWeight: 'bold', fontSize: '14px' }}>Фильтр по языку:</span>
          <Radio.Group
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="all">
              Все ({documents.length})
            </Radio.Button>
            <Radio.Button value="ru">
              🇷🇺 Русский ({documents.filter(d => { const l = d.language || 'Русский'; return l === 'Русский' || l === 'ru'; }).length})
            </Radio.Button>
            <Radio.Button value="tg">
              🇹🇯 Тоҷикӣ ({documents.filter(d => { const l = d.language || 'Русский'; return l === 'Таджикский' || l === 'tg'; }).length})
            </Radio.Button>
          </Radio.Group>
        </Space>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={languageFilter === 'all' ? documents : documents.filter(d => {
            const lang = d.language || 'Русский';
            if (languageFilter === 'ru') return lang === 'Русский' || lang === 'ru';
            if (languageFilter === 'tg') return lang === 'Таджикский' || lang === 'tg';
            return true;
          })}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} документов`
          }}
        />
      </Card>

      <Modal
        title={editingDocument ? 'Редактировать закон' : 'Добавить закон'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="title"
            label="Название документа"
            rules={[{ required: true, message: 'Введите название' }]}
          >
            <Input placeholder="Введите название документа" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Описание"
            rules={[{ required: true, message: 'Введите описание' }]}
          >
            <TextArea rows={3} placeholder="Краткое описание документа" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Тип документа"
            rules={[{ required: true, message: 'Выберите тип' }]}
          >
            <Select placeholder="Выберите тип документа">
              <Option value="Конвенция">Конвенция</Option>
              <Option value="Закон">Закон</Option>
              <Option value="Руководство">Руководство</Option>
              <Option value="Инструкция">Инструкция</Option>
              <Option value="Памятка">Памятка</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="language"
            label="Язык"
            rules={[{ required: true, message: 'Выберите язык' }]}
          >
            <Select placeholder="Выберите язык">
              <Option value="Таджикский">Таджикский</Option>
              <Option value="Русский">Русский</Option>
              <Option value="Английский">Английский</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="Статус"
            valuePropName="checked"
            getValueFromEvent={(checked) => checked ? 'active' : 'inactive'}
            getValueProps={(value) => ({ checked: value === 'active' })}
          >
            <Switch checkedChildren="Активен" unCheckedChildren="Неактивен" />
          </Form.Item>

          <Form.Item
            name="iconFile"
            label="Иконка документа"
            tooltip="Загрузите изображение иконки, которая будет отображаться для этого документа"
          >
            <Upload
              name="iconFile"
              listType="picture-card"
              maxCount={1}
              beforeUpload={() => false}
              accept="image/*"
            >
              <div>
                <UploadOutlined />
                <div style={{ marginTop: 8 }}>Загрузить</div>
              </div>
            </Upload>
          </Form.Item>
          
          <Form.Item
            name="iconUrl"
            label="Или выберите стандартную иконку"
            tooltip="Выберите стандартную иконку из списка, если не загружаете свою"
          >
            <Select placeholder="Выберите стандартную иконку (опционально)">
              <Option value="">Не использовать стандартную иконку</Option>
              <Option value="assets/images/un_convention.png">Конвенция ООН</Option>
              <Option value="assets/images/tj_law.png">Закон Таджикистана</Option>
              <Option value="assets/images/book.png">Книга</Option>
              <Option value="assets/images/judge.png">Судья</Option>
              <Option value="assets/images/user_check.png">Проверка пользователя</Option>
              <Option value="assets/images/verify.png">Верификация</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="content"
            label="Текстовое содержимое"
            tooltip="Введите текст документа. Если заполнено, документ будет отображаться как текст. Если не заполнено, будет использован файл PDF."
          >
            <TextArea 
              rows={8} 
              placeholder="Введите полный текст документа здесь..." 
            />
          </Form.Item>

          <Form.Item
            name="file"
            label="PDF файл документа (опционально)"
            tooltip="Загрузите PDF файл, если документ должен быть в формате PDF. Если заполнено текстовое содержимое, PDF будет использован как альтернатива."
          >
            <Upload.Dragger
              name="file"
              multiple={false}
              beforeUpload={() => false}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Нажмите или перетащите PDF файл сюда</p>
              <p className="ant-upload-hint">
                Поддерживается формат: PDF
              </p>
            </Upload.Dragger>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Documents;
